// What the SERVER says this tier currently permits, applied to the Control
// Plane's governed writes.
//
// ─── Why this is read at runtime, not baked into the build ────────────────
//
// `FEATURE_ADMIN_WRITES` lives in the Worker's environment. The console cannot
// see it, and a build-time guess is wrong in both directions at the worst
// moment: a bundle built while the gate was closed would keep the governed
// actions hidden after an operator opened it, and a bundle built while it was
// open would keep offering buttons after it closed — each one requiring a
// rebuild to tell the truth again.
//
// So the server states it, on the session read the console already makes, and
// this component turns that statement into the Control Plane's authority
// context. Flipping the gate on a deployed tier changes what the SAME shipped
// bundle offers on the next session read.
//
// ─── Fail-closed, in every direction ──────────────────────────────────────
//
// Closed before the first answer arrives, closed without a session, closed on
// a network or auth failure, closed on a response whose shape is not the one
// `parseCapabilities` recognises. An "open" is only ever the result of the
// server having said the literal boolean.
//
// ─── This is not authorization ────────────────────────────────────────────
//
// It decides what the interface OFFERS. Every governed write is decided again
// on the server against the same gate, the session, live GaoSafe membership,
// the proposal's operation/product/lane/target binding and the approval
// threshold. Forcing this value true in a modified frontend changes the
// appearance of its own buttons and nothing about what the Worker accepts.

import { useEffect, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { apiCall } from "../api/client";
import { getSessionToken, subscribeSessionToken } from "../auth/session";
import { parseCapabilities } from "../auth/session-claims";
import { ControlPlaneAuthorityProvider } from "../authority";

export function ControlPlaneAuthorityGate({ children }: { children: ReactNode }) {
  const [allowWrites, setAllowWrites] = useState(false);
  const location = useLocation();
  // Re-read when the operator signs in or out, as well as on navigation, so a
  // session that appears mid-visit does not leave the capability stuck closed
  // until the next route change.
  const [tokenEpoch, setTokenEpoch] = useState(0);

  useEffect(() => subscribeSessionToken(() => setTokenEpoch((n) => n + 1)), []);

  useEffect(() => {
    let cancelled = false;

    // No session, nothing to ask about — and asking would only produce a 401
    // on the login screen.
    if (getSessionToken() === null) {
      setAllowWrites(false);
      return;
    }

    void (async () => {
      try {
        const body = await apiCall<unknown>("/v2/admin/bff/session", { method: "GET" });
        if (!cancelled) setAllowWrites(parseCapabilities(body).controlPlaneWrites);
      } catch {
        // Includes 401 (the session died), a network failure, and a body the
        // parser rejected. All of them mean: do not offer a governed write.
        if (!cancelled) setAllowWrites(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // `location.pathname` is the re-read trigger: arriving at a Control Plane
    // or approval screen asks the server again, so a gate closed since the last
    // look is reflected without a reload.
  }, [location.pathname, tokenEpoch]);

  return (
    <ControlPlaneAuthorityProvider allowWrites={allowWrites}>
      {children}
    </ControlPlaneAuthorityProvider>
  );
}
